import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import {
  createInvoice,
  updateInvoice,
  fetchUnbilledExpenses,
  fetchBillingRates,
  resolveBillingRate,
} from '../../store/actions/FinanceActions';
import { listClients } from '../../store/actions/ClientActions';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Badge from '../shared/TailwindComponents/Badge';
import Toggle from '../shared/TailwindComponents/Toggle';
import {
  PiXLight,
  PiPlusLight,
  PiTrashLight,
  PiReceiptLight,
  PiArrowLeftLight,
  PiArrowRightLight,
  PiDownloadLight,
  PiCheckLight,
  PiUserLight,
  PiListLight,
  PiWarningLight,
} from 'react-icons/pi';
import { FinanceExpense, FinanceBillingRate, FinanceInvoice } from '../../store/reducer/FinanceReducer';
import { useOrgMembers } from '../../hooks/useOrgMembers';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  organizationId: number;
  editInvoice?: FinanceInvoice | null;
}

interface LineItem {
  description: string;
  amount: string;
  quantity: string;
  assigneeId?: string;
  assigneeName?: string;
  rateId?: number;
}

const STEPS = ['client', 'items', 'adjustments', 'preview'] as const;
type Step = typeof STEPS[number];

const EXPENSE_CATEGORIES: Record<string, string> = {
  filing_fee: 'Filing Fee',
  court_cost: 'Court Cost',
  travel: 'Travel',
  expert_witness: 'Expert Witness',
  copying: 'Copying',
  postage: 'Postage',
  research: 'Research',
  other: 'Other',
};

const formatCurrency = (amount: number, currency: string = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const CreateInvoiceModal: React.FC<Props> = ({ isOpen, onClose, onCreated, organizationId, editInvoice }) => {
  const isEdit = !!editInvoice;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  const unbilledExpenses = useAppSelector((state: RootState) => state.finance.unbilledExpenses);
  const billingRates = useAppSelector((state: RootState) => state.finance.billingRates);
  const matters = useAppSelector((state: RootState) => state.matters?.matters || []);
  const preferredCurrency = useAppSelector((state: RootState) => state.user?.userInfo?.preferences?.general_settings?.currency) || 'usd';
  const { members: orgMembers } = useOrgMembers();

  const [step, setStep] = useState<Step>('client');
  const [clients, setClients] = useState<any[]>([]);
  const [showRatesPicker, setShowRatesPicker] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);

  // Step 1: Client & Matter
  const [clientId, setClientId] = useState<number | null>(null);
  const [matterId, setMatterId] = useState<number | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');

  // Step 2: Line Items
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: '', amount: '', quantity: '1' },
  ]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<number[]>([]);
  const [showExpenseImport, setShowExpenseImport] = useState(false);

  // Step 3: Adjustments
  const [taxRate, setTaxRate] = useState('');
  const [discountRate, setDiscountRate] = useState('');
  const [dueDays, setDueDays] = useState('30');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [notes, setNotes] = useState('');
  const [internalMemo, setInternalMemo] = useState('');
  const [currency, setCurrency] = useState(preferredCurrency || 'usd');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    line1: '', line2: '', city: '', state: '', postal_code: '', country: '',
  });

  // Pre-populate form when editing
  useEffect(() => {
    if (isOpen && editInvoice) {
      setClientId(editInvoice.client_id || null);
      setMatterId(editInvoice.matter_id || null);
      setClientName(editInvoice.client_name || '');
      setClientEmail(editInvoice.client_email || '');
      setCurrency(editInvoice.currency || preferredCurrency || 'usd');
      setNotes(editInvoice.notes || '');
      setInternalMemo(editInvoice.internal_memo || '');
      setPaymentTerms(editInvoice.payment_terms || '');
      setIsRecurring(editInvoice.is_recurring || false);
      setRecurringInterval(editInvoice.recurring_interval || '');
      setTaxRate(editInvoice.tax_rate ? String(editInvoice.tax_rate * 100) : '');
      setDiscountRate(editInvoice.discount_rate ? String(editInvoice.discount_rate * 100) : '');

      // Parse due days from payment_terms "Net X" or from due_date
      if (editInvoice.payment_terms?.match(/Net\s*(\d+)/i)) {
        setDueDays(editInvoice.payment_terms.match(/Net\s*(\d+)/i)![1]);
      }

      // Billing address
      if (editInvoice.billing_address && typeof editInvoice.billing_address === 'object') {
        setBillingAddress({
          line1: editInvoice.billing_address.line1 || '',
          line2: editInvoice.billing_address.line2 || '',
          city: editInvoice.billing_address.city || '',
          state: editInvoice.billing_address.state || '',
          postal_code: editInvoice.billing_address.postal_code || '',
          country: editInvoice.billing_address.country || '',
        });
      }

      // Line items — amounts stored in cents, convert back
      if (editInvoice.line_items?.length) {
        setLineItems(
          editInvoice.line_items.map((li: any) => ({
            description: li.description || '',
            amount: li.amount ? String(li.amount / 100) : '',
            quantity: String(li.quantity || 1),
            assigneeId: li.assigneeId || undefined,
            assigneeName: li.assigneeName || undefined,
            rateId: li.rateId || undefined,
          }))
        );
      }

      setStep('items'); // skip client step, go straight to items
    }
  }, [isOpen, editInvoice]);

  // Load clients, billing rates on open
  useEffect(() => {
    if (isOpen) {
      listClients(1, 200).then((res) => {
        if (res?.clients) setClients(res.clients);
      });
      dispatch(fetchMatters({ page: 1, page_size: 200 }));
      if (organizationId) {
        dispatch(fetchBillingRates(organizationId));
      }
    }
  }, [isOpen, dispatch, organizationId]);

  // Load unbilled expenses when client/matter changes
  useEffect(() => {
    if (isOpen && organizationId) {
      dispatch(fetchUnbilledExpenses(organizationId, clientId || undefined, matterId || undefined));
    }
  }, [isOpen, organizationId, clientId, matterId, dispatch]);

  // Auto-fill client info
  useEffect(() => {
    if (clientId) {
      const client = clients.find((c: any) => (c.id || c.ClientID) == clientId);
      if (client) {
        setClientName(client.name || client.Name || '');
        setClientEmail(client.contact_email || client.Email || '');
      }
    } else {
      setClientName('');
      setClientEmail('');
    }
  }, [clientId, clients]);

  const clientOptions = useMemo(() =>
    clients.map((c: any) => ({
      value: String(c.id || c.ClientID),
      label: c.name || c.Name || 'Unnamed Client',
    })),
    [clients]
  );

  const matterOptions = useMemo(() => {
    if (!clientId) {
      return matters.map((m: any) => ({
        value: String(m.id || m.MatterID),
        label: m.title || m.Title || 'Untitled Matter',
      }));
    }

    // Find selected client to get their UUID for matter_clients matching
    const selectedClient = clients.find((c: any) => String(c.id || c.ClientID) === String(clientId));
    const selectedClientUuid = selectedClient?.uuid || selectedClient?.client_uuid;
    const selectedClientName = selectedClient?.name || selectedClient?.Name;

    const filtered = matters.filter((m: any) => {
      // 1. Check direct client_id (legacy field)
      const mClientId = m.clientId || m.client_id || m.ClientID;
      if (mClientId != null && String(mClientId) === String(clientId)) return true;

      // 2. Check matter_clients relationship (current approach)
      const mClients = m.matter_clients?.clients || (Array.isArray(m.matter_clients) ? m.matter_clients : []);
      if (mClients.length > 0) {
        return mClients.some((mc: any) => {
          if (selectedClientUuid && mc.client_uuid) return mc.client_uuid === selectedClientUuid;
          if (selectedClientName && mc.name) return mc.name === selectedClientName;
          return false;
        });
      }

      // 3. Check Clients array (PascalCase legacy)
      if (Array.isArray(m.Clients)) {
        return m.Clients.some((c: any) => String(c.id || c.ClientID) === String(clientId));
      }

      return false;
    });

    return filtered.map((m: any) => ({
      value: String(m.id || m.MatterID),
      label: m.title || m.Title || 'Untitled Matter',
    }));
  }, [matters, clientId, clients]);

  // ── Line Item Management ──────────────────────────────────

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', amount: '', quantity: '1' }]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: string) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  /** Assign a team member to a line item and auto-resolve their billing rate */
  const assignTeamMember = async (index: number, memberId: string) => {
    const member = orgMembers.find(m => m.id === memberId);
    const updated = [...lineItems];
    updated[index] = {
      ...updated[index],
      assigneeId: memberId || undefined,
      assigneeName: member?.name || undefined,
    };
    setLineItems(updated);

    // Auto-resolve billing rate for this member
    if (memberId && organizationId) {
      const rate = await resolveBillingRate({
        organization_id: organizationId,
        user_id: parseInt(memberId),
        client_id: clientId || undefined,
        matter_id: matterId || undefined,
      });
      if (rate) {
        const rateAmount = rate.rate_type === 'contingency'
          ? (rate.amount / 100).toFixed(1)
          : (rate.amount / 100).toFixed(2);
        const updatedWithRate = [...lineItems];
        updatedWithRate[index] = {
          ...updatedWithRate[index],
          assigneeId: memberId,
          assigneeName: member?.name || undefined,
          amount: rateAmount,
          rateId: rate.id,
          description: updatedWithRate[index].description || (rate.description || `${member?.name || ''} — ${rate.rate_type}`),
        };
        setLineItems(updatedWithRate);
      }
    }
  };

  /** Add a line item pre-populated from an existing billing rate / service */
  const addFromRate = (rate: FinanceBillingRate) => {
    const rateAmount = rate.rate_type === 'contingency'
      ? (rate.amount / 100).toFixed(1)
      : (rate.amount / 100).toFixed(2);
    const newItem: LineItem = {
      description: rate.description || `${rate.rate_type} rate`,
      amount: rateAmount,
      quantity: '1',
      rateId: rate.id,
    };
    setLineItems([...lineItems.filter(li => li.description || li.amount), newItem]);
    setShowRatesPicker(false);
  };

  /** Add a team member as a new line item with their resolved rate */
  const addTeamMemberItem = async (memberId: string) => {
    const member = orgMembers.find(m => m.id === memberId);
    if (!member) return;

    const newItem: LineItem = {
      description: `${member.name}`,
      amount: '',
      quantity: '1',
      assigneeId: memberId,
      assigneeName: member.name,
    };

    if (organizationId) {
      const rate = await resolveBillingRate({
        organization_id: organizationId,
        user_id: parseInt(memberId),
        client_id: clientId || undefined,
        matter_id: matterId || undefined,
      });
      if (rate) {
        newItem.amount = rate.rate_type === 'contingency'
          ? (rate.amount / 100).toFixed(1)
          : (rate.amount / 100).toFixed(2);
        newItem.rateId = rate.id;
        newItem.description = rate.description || `${member.name} — ${rate.rate_type}`;
      }
    }

    setLineItems([...lineItems.filter(li => li.description || li.amount), newItem]);
  };

  const importExpenses = () => {
    const newItems: LineItem[] = unbilledExpenses
      .filter((e: FinanceExpense) => selectedExpenseIds.includes(e.id))
      .map((e: FinanceExpense) => ({
        description: `${EXPENSE_CATEGORIES[e.category] || e.category}: ${e.description || ''}`.trim(),
        amount: String(e.amount / 100),
        quantity: '1',
      }));
    setLineItems([...lineItems.filter(li => li.description || li.amount), ...newItems]);
    setShowExpenseImport(false);
  };

  const toggleExpense = (id: number) => {
    setSelectedExpenseIds((prev) =>
      prev.includes(id) ? prev.filter((eid) => eid !== id) : [...prev, id]
    );
  };

  // ── Calculations ──────────────────────────────────────────

  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      const qty = parseInt(item.quantity) || 1;
      return sum + amount * qty * 100;
    }, 0);
  }, [lineItems]);

  const taxAmount = useMemo(() => {
    const rate = parseFloat(taxRate) || 0;
    return Math.round(subtotal * (rate / 100));
  }, [subtotal, taxRate]);

  const discountAmount = useMemo(() => {
    const rate = parseFloat(discountRate) || 0;
    return Math.round(subtotal * (rate / 100));
  }, [subtotal, discountRate]);

  const totalAmount = subtotal + taxAmount - discountAmount;

  // ── Navigation ────────────────────────────────────────────

  const stepIndex = STEPS.indexOf(step);
  const canGoNext = () => {
    if (step === 'items') {
      return lineItems.some((li) => li.description && li.amount);
    }
    return true;
  };

  const goNext = () => {
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1]);
  };

  const goBack = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) setStep(STEPS[idx - 1]);
  };

  // ── Submit ────────────────────────────────────────────────

  const handleSubmit = async () => {
    const items = lineItems
      .filter((item) => item.description && item.amount)
      .map((item) => ({
        description: item.description,
        amount: Math.round(parseFloat(item.amount) * 100),
        quantity: parseInt(item.quantity) || 1,
      }));

    if (items.length === 0) return;

    const hasAddress = Object.values(billingAddress).some((v) => v.trim());

    const payload = {
      organization_id: organizationId,
      client_id: clientId || undefined,
      matter_id: matterId || undefined,
      line_items: items,
      currency,
      due_days: parseInt(dueDays) || 30,
      notes: notes || undefined,
      tax_rate: taxRate ? parseFloat(taxRate) / 100 : undefined,
      discount_rate: discountRate ? parseFloat(discountRate) / 100 : undefined,
      internal_memo: internalMemo || undefined,
      billing_address: hasAddress ? billingAddress : undefined,
      payment_terms: paymentTerms || undefined,
      is_recurring: isRecurring,
      recurring_interval: recurringInterval || undefined,
      expense_ids: selectedExpenseIds.length > 0 ? selectedExpenseIds : undefined,
    };

    const result = isEdit && editInvoice
      ? await dispatch(updateInvoice(editInvoice.id, payload))
      : await dispatch(createInvoice(payload));

    if (result) {
      onCreated();
      resetForm();
    }
  };

  const resetForm = () => {
    setStep('client');
    setClientId(null);
    setMatterId(null);
    setLineItems([{ description: '', amount: '', quantity: '1' }]);
    setSelectedExpenseIds([]);
    setShowRatesPicker(false);
    setShowDiscardWarning(false);
    setTaxRate('');
    setDiscountRate('');
    setDueDays('30');
    setPaymentTerms('');
    setNotes('');
    setInternalMemo('');
    setIsRecurring(false);
    setRecurringInterval('');
    setBillingAddress({ line1: '', line2: '', city: '', state: '', postal_code: '', country: '' });
  };

  const hasUnsavedChanges = useMemo(() => {
    return !!(
      clientId || matterId || clientName || clientEmail ||
      lineItems.some((li) => li.description || li.amount) ||
      taxRate || discountRate || notes || internalMemo ||
      Object.values(billingAddress).some((v) => v.trim())
    );
  }, [clientId, matterId, clientName, clientEmail, lineItems, taxRate, discountRate, notes, internalMemo, billingAddress]);

  const handleBackdropClick = () => {
    if (hasUnsavedChanges) {
      setShowDiscardWarning(true);
    } else {
      onClose();
      resetForm();
    }
  };

  const confirmDiscard = () => {
    setShowDiscardWarning(false);
    onClose();
    resetForm();
  };

  // ── Memos (must be before early return to keep hook count stable) ──

  const memberOptions = useMemo(() =>
    orgMembers.map(m => ({
      value: m.id,
      label: m.name,
    })),
    [orgMembers]
  );

  const rateOptions = useMemo(() => [
    { value: '', label: t('Custom item') },
    ...billingRates.map((rate: FinanceBillingRate) => ({
      value: String(rate.id),
      label: `${rate.description || rate.rate_type} — ${rate.rate_type === 'contingency' ? `${(rate.amount / 100).toFixed(1)}%` : formatCurrency(rate.amount, rate.currency)}`,
    })),
  ], [billingRates, t]);

  if (!isOpen) return null;

  // ── Step Content ──────────────────────────────────────────

  const renderStepIndicator = () => (
    <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <button
            onClick={() => {
              if (i <= stepIndex || (i === stepIndex + 1 && canGoNext())) setStep(s);
            }}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              s === step
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : i < stepIndex
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            }`}
          >
            <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold ${
              i < stepIndex ? 'bg-green-500 text-white' : s === step ? 'bg-primary-600 text-white' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
            }`}>
              {i < stepIndex ? <PiCheckLight className="w-3 h-3" /> : i + 1}
            </span>
            {s === 'client' ? t('Client & Matter') : s === 'items' ? t('Line Items') : s === 'adjustments' ? t('Adjustments') : t('Preview')}
          </button>
          {i < STEPS.length - 1 && <div className="w-6 h-px bg-gray-300 dark:bg-gray-600" />}
        </React.Fragment>
      ))}
    </div>
  );

  const renderClientStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">{t('Select Client')}</h3>
          <Dropdown
            options={[{ value: '', label: t('No Client (Manual)') }, ...clientOptions]}
            value={clientId ? String(clientId) : ''}
            onChange={(val) => { setClientId(val ? Number(val) : null); setMatterId(null); }}
            placeholder={t('Choose a client...')}
          />
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">{t('Select Matter')}</h3>
          <Dropdown
            options={[{ value: '', label: t('No Specific Matter') }, ...matterOptions]}
            value={matterId ? String(matterId) : ''}
            onChange={(val) => setMatterId(val ? Number(val) : null)}
            placeholder={t('Choose a matter...')}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label={t('Client Name')}
          type="text"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder={t('Auto-filled from client')}
        />
        <Input
          label={t('Client Email')}
          type="email"
          value={clientEmail}
          onChange={(e) => setClientEmail(e.target.value)}
          placeholder={t('For sending invoice')}
        />
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">{t('Billing Address')}</h3>
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="text"
            placeholder={t('Address Line 1')}
            value={billingAddress.line1}
            onChange={(e) => setBillingAddress((p) => ({ ...p, line1: e.target.value }))}
          />
          <Input
            type="text"
            placeholder={t('Address Line 2')}
            value={billingAddress.line2}
            onChange={(e) => setBillingAddress((p) => ({ ...p, line2: e.target.value }))}
          />
          <Input
            type="text"
            placeholder={t('City')}
            value={billingAddress.city}
            onChange={(e) => setBillingAddress((p) => ({ ...p, city: e.target.value }))}
          />
          <Input
            type="text"
            placeholder={t('State / Region')}
            value={billingAddress.state}
            onChange={(e) => setBillingAddress((p) => ({ ...p, state: e.target.value }))}
          />
          <Input
            type="text"
            placeholder={t('Postal Code')}
            value={billingAddress.postal_code}
            onChange={(e) => setBillingAddress((p) => ({ ...p, postal_code: e.target.value }))}
          />
          <Input
            type="text"
            placeholder={t('Country')}
            value={billingAddress.country}
            onChange={(e) => setBillingAddress((p) => ({ ...p, country: e.target.value }))}
          />
        </div>
      </div>
    </div>
  );


  const addFromRateById = (rateId: string, index: number) => {
    const rate = billingRates.find((r: FinanceBillingRate) => String(r.id) === rateId);
    if (!rate) return;
    const rateAmount = rate.rate_type === 'contingency'
      ? (rate.amount / 100).toFixed(1)
      : (rate.amount / 100).toFixed(2);
    const updated = [...lineItems];
    updated[index] = {
      ...updated[index],
      description: rate.description || `${rate.rate_type} rate`,
      amount: rateAmount,
      rateId: rate.id,
    };
    setLineItems(updated);
  };

  const renderItemsStep = () => (
    <div className="space-y-5">
      {/* Header with global actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">{t('Line Items')}</h3>
        <div className="flex gap-2">
          {unbilledExpenses.length > 0 && (
            <Button variant="outline" size="small" onClick={() => setShowExpenseImport(!showExpenseImport)}>
              <PiDownloadLight className="w-3.5 h-3.5 mr-1" />
              {t('Import Expenses')} ({unbilledExpenses.length})
            </Button>
          )}
        </div>
      </div>

      {/* Expense Import Panel */}
      {showExpenseImport && (
        <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/10">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-medium text-blue-700 dark:text-blue-400">{t('Unbilled Expenses')}</h4>
            <Button variant="primary" size="small" onClick={importExpenses} disabled={selectedExpenseIds.length === 0}>
              {t('Import Selected')} ({selectedExpenseIds.length})
            </Button>
          </div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {unbilledExpenses.map((exp: FinanceExpense) => (
              <label
                key={exp.id}
                className="flex items-center gap-3 p-2 rounded-md hover:bg-blue-100/50 dark:hover:bg-blue-900/20 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedExpenseIds.includes(exp.id)}
                  onChange={() => toggleExpense(exp.id)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                  {EXPENSE_CATEGORIES[exp.category] || exp.category}: {exp.description || '—'}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatCurrency(exp.amount, exp.currency)}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Line Item Cards */}
      <div className="space-y-3">
        {lineItems.map((item, index) => (
          <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-400 dark:text-gray-500">#{index + 1}</span>
              <button
                type="button"
                onClick={() => removeLineItem(index)}
                className="p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                disabled={lineItems.length <= 1}
              >
                <PiTrashLight className="w-4 h-4" />
              </button>
            </div>

            {/* Assignee + Service / Rate selector */}
            <div className={`grid gap-3 ${billingRates.length > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('Assignee')}</label>
                <Dropdown
                  options={[{ value: '', label: t('Unassigned') }, ...memberOptions]}
                  value={item.assigneeId || ''}
                  onChange={(val) => assignTeamMember(index, val as string)}
                  placeholder={t('Unassigned')}
                />
              </div>
              {billingRates.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('Service / Rate')}</label>
                  <Dropdown
                    options={rateOptions}
                    value={item.rateId ? String(item.rateId) : ''}
                    onChange={(val) => {
                      if (val) addFromRateById(val as string, index);
                      else updateLineItem(index, 'rateId' as any, '');
                    }}
                    placeholder={t('Select a service...')}
                  />
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('Description')}</label>
              <Input
                type="text"
                placeholder={t('Describe the service or item...')}
                value={item.description}
                onChange={(e) => updateLineItem(index, 'description', e.target.value)}
              />
            </div>

            {/* Amount + Quantity */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('Rate / Amount')}</label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={item.amount}
                  onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('Qty / Hours')}</label>
                <Input
                  type="number"
                  placeholder="1"
                  value={item.quantity}
                  onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                />
              </div>
            </div>

            {/* Line total */}
            {(item.amount && item.quantity) && (
              <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                {t('Line Total')}: <span className="font-medium text-gray-900 dark:text-white">{formatCurrency((parseFloat(item.amount) || 0) * (parseInt(item.quantity) || 1) * 100, currency)}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add item button */}
      <button
        type="button"
        onClick={addLineItem}
        className="w-full py-2.5 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors flex items-center justify-center gap-1.5"
      >
        <PiPlusLight className="w-4 h-4" />
        {t('Add Line Item')}
      </button>

      {/* Running total */}
      <div className="flex justify-end pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm font-medium text-gray-900 dark:text-white">
          {t('Subtotal')}: {formatCurrency(subtotal, currency)}
        </div>
      </div>
    </div>
  );

  const renderAdjustmentsStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <Input
          label={t('Tax Rate (%)')}
          type="number"
          value={taxRate}
          onChange={(e) => setTaxRate(e.target.value)}
          placeholder="0"
        />
        <Input
          label={t('Discount Rate (%)')}
          type="number"
          value={discountRate}
          onChange={(e) => setDiscountRate(e.target.value)}
          placeholder="0"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label={t('Due In (days)')}
          type="number"
          value={dueDays}
          onChange={(e) => setDueDays(e.target.value)}
        />
        <Input
          label={t('Payment Terms')}
          type="text"
          value={paymentTerms}
          onChange={(e) => setPaymentTerms(e.target.value)}
          placeholder={t('e.g., Net 30')}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('Currency')}</label>
          <Dropdown
            options={[
              { value: 'usd', label: 'USD' },
              { value: 'eur', label: 'EUR' },
              { value: 'gbp', label: 'GBP' },
              { value: 'nok', label: 'NOK' },
              { value: 'sek', label: 'SEK' },
            ]}
            value={currency}
            onChange={(val) => setCurrency(val as string)}
          />
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <Toggle enabled={isRecurring} onToggle={setIsRecurring} />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('Recurring Invoice')}</span>
          </div>
          {isRecurring && (
            <Dropdown
              options={[
                { value: 'weekly', label: t('Weekly') },
                { value: 'monthly', label: t('Monthly') },
                { value: 'quarterly', label: t('Quarterly') },
                { value: 'yearly', label: t('Yearly') },
              ]}
              value={recurringInterval}
              onChange={(val) => setRecurringInterval(val as string)}
              placeholder={t('Interval')}
              compact
            />
          )}
        </div>
      </div>

      <Textarea
        id="invoice-notes"
        label={t('Client-Facing Notes')}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t('Notes visible on the invoice...')}
        rows={2}
      />

      <Textarea
        id="invoice-memo"
        label={t('Internal Memo')}
        value={internalMemo}
        onChange={(e) => setInternalMemo(e.target.value)}
        placeholder={t('Internal notes (not visible to client)...')}
        rows={2}
      />
    </div>
  );

  const renderPreviewStep = () => {
    const validItems = lineItems.filter((li) => li.description && li.amount);
    return (
      <div className="space-y-6">
        {/* Invoice preview card */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-900">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('Invoice Preview')}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('Draft — Not yet sent')}</p>
            </div>
            <Badge color="gray">{t('Draft')}</Badge>
          </div>

          {/* Client info */}
          {(clientName || clientEmail) && (
            <div className="mb-6">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{t('Bill To')}</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{clientName || '—'}</p>
              {clientEmail && <p className="text-sm text-gray-600 dark:text-gray-400">{clientEmail}</p>}
              {billingAddress.line1 && (
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  <p>{billingAddress.line1}</p>
                  {billingAddress.line2 && <p>{billingAddress.line2}</p>}
                  <p>
                    {[billingAddress.city, billingAddress.state, billingAddress.postal_code]
                      .filter(Boolean).join(', ')}
                  </p>
                  {billingAddress.country && <p>{billingAddress.country}</p>}
                </div>
              )}
            </div>
          )}

          {/* Line items table — full width, no rounded corners */}
          <div className="border-y border-gray-200 dark:border-gray-700 overflow-hidden -mx-6 mb-4">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="text-left px-6 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">{t('Description')}</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">{t('Assignee')}</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">{t('Qty')}</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">{t('Rate')}</th>
                  <th className="text-right px-6 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">{t('Amount')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {validItems.map((item, i) => {
                  const amt = parseFloat(item.amount) || 0;
                  const qty = parseInt(item.quantity) || 1;
                  return (
                    <tr key={i}>
                      <td className="px-6 py-2 text-gray-900 dark:text-white">{item.description}</td>
                      <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                        {item.assigneeName ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <PiUserLight className="w-3 h-3" />
                            {item.assigneeName}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{qty}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{formatCurrency(amt * 100, currency)}</td>
                      <td className="px-6 py-2 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(amt * qty * 100, currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{t('Subtotal')}</span>
                <span className="font-normal text-gray-900 dark:text-white">{formatCurrency(subtotal, currency)}</span>
              </div>
              {taxAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">{t('Tax')} ({taxRate}%)</span>
                  <span className="font-normal text-gray-900 dark:text-white">{formatCurrency(taxAmount, currency)}</span>
                </div>
              )}
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">{t('Discount')} ({discountRate}%)</span>
                  <span className="font-normal text-green-600 dark:text-green-400">-{formatCurrency(discountAmount, currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="text-gray-900 dark:text-white">{t('Total')}</span>
                <span className="text-gray-900 dark:text-white">{formatCurrency(totalAmount, currency)}</span>
              </div>
            </div>
          </div>

          {/* Meta — badges */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Badge color="gray">{t('Due')}: {dueDays} {t('days')}</Badge>
            <Badge color="gray">{currency.toUpperCase()}</Badge>
            {paymentTerms && <Badge color="blue">{paymentTerms}</Badge>}
            {isRecurring && recurringInterval && (
              <Badge color="purple">{t('Recurring')}: {recurringInterval}</Badge>
            )}
            {taxRate && parseFloat(taxRate) > 0 && (
              <Badge color="gray">{t('Tax')}: {taxRate}%</Badge>
            )}
            {discountRate && parseFloat(discountRate) > 0 && (
              <Badge color="green">{t('Discount')}: {discountRate}%</Badge>
            )}
          </div>

          {notes && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('Notes')}</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{notes}</p>
            </div>
          )}
        </div>

        {selectedExpenseIds.length > 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('{{count}} expenses will be marked as invoiced', { count: selectedExpenseIds.length })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleBackdropClick} />

      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-2xl flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{isEdit ? t('Edit Invoice') : t('Create Invoice')}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {isEdit ? t('Update your invoice details') : t('Build a professional invoice step by step')}
            </p>
          </div>
          <button
            onClick={handleBackdropClick}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <PiXLight className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Step indicator */}
        {renderStepIndicator()}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-[#f5f5f5] dark:bg-gray-800">
          {step === 'client' && renderClientStep()}
          {step === 'items' && renderItemsStep()}
          {step === 'adjustments' && renderAdjustmentsStep()}
          {step === 'preview' && renderPreviewStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div>
            {stepIndex > 0 && (
              <Button variant="outline" size="small" onClick={goBack}>
                <PiArrowLeftLight className="w-4 h-4 mr-1" />
                {t('Back')}
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" size="small" onClick={handleBackdropClick}>
              {t('Cancel')}
            </Button>
            {step === 'preview' ? (
              <Button
                variant="primary"
                size="small"
                onClick={handleSubmit}
                isLoading={isEdit ? loading?.updateInvoice : loading?.createInvoice}
              >
                <PiReceiptLight className="w-4 h-4 mr-1" />
                {isEdit ? t('Update Invoice') : t('Create Invoice')}
              </Button>
            ) : (
              <Button variant="primary" size="small" onClick={goNext} disabled={!canGoNext()}>
                {t('Next')}
                <PiArrowRightLight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>

        {/* Discard Warning Overlay */}
        {showDiscardWarning && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/30 rounded-lg" onClick={() => setShowDiscardWarning(false)} />
            <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 max-w-sm mx-auto text-center space-y-4">
              <div className="mx-auto w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <PiWarningLight className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('Unsaved Changes')}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('You have unsaved changes. Are you sure you want to discard them?')}</p>
              </div>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" size="small" onClick={() => setShowDiscardWarning(false)}>
                  {t('Keep Editing')}
                </Button>
                <Button variant="primary" size="small" onClick={confirmDiscard}>
                  {t('Discard')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateInvoiceModal;
